import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setGlobalElement, setModalData } from "@/store/globalSlice"
import { allPieces, images } from "@/utils/data"
import classNames from "classnames"
import dummyFlag from "@/images/boards/dummy-flag.png"
import Money from "@/Components/Money"
import { useEffect, useRef } from "react"
import { mapDesigns } from "@mono/mapDesigns"

import { SignalSlashIcon } from "@heroicons/react/24/solid"
import { gameRoles } from "@/utils/gameRoles"
import { getUserCreds } from "@/utils/utils"
import { getPlayerAvatar } from "@/utils/playerAvatar"
const tempUserImage =
  "https://plus.unsplash.com/premium_photo-1671656349322-41de944d259b?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8dXNlcnxlbnwwfHwwfHx8MA%3D%3D"

export interface PlayerListItemInfoProps {
  player: IPlayer
  user: IUser
  distinguishActivePlayer?: boolean
  forTradeScreen?: boolean
  forAuctionScreen?: boolean
}

const PlayerListItemInfo: React.FC<PlayerListItemInfoProps> = ({
  player,
  user,
  distinguishActivePlayer = true,
  forTradeScreen = false,
  forAuctionScreen = false,
}) => {
  const piece = allPieces.find((item) => item.id === player.pieceId)
  const wrapperRef = useRef(null)
  const dispatch = useAppDispatch()
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const currentTurn = useAppSelector(
    (state) => state.global.myGame?.situation?.turn
  )
  const playerListItems = useAppSelector(
    (state) => state.global.elements.playerListItems
  )
  const map = mapDesigns.find((map) => map.id === mapId)
  const moneyAnimations = useAppSelector(
    (state) => state.global.moneyAnimations
  )

  useEffect(() => {
    if (!wrapperRef.current) return
    if (
      playerListItems.find((elm) => elm.order === player.order)
    )
      return
    dispatch(
      setGlobalElement({
        playerListItems: [
          ...playerListItems,
          { order: player.order, element: wrapperRef.current },
        ],
      })
    )
  }, [playerListItems, dispatch, player.order])
  const moneyDecreaseAnimation = moneyAnimations.find(
    (anim) => anim.from === player.order
  )
  const moneyIncreaseAnimation = moneyAnimations.find(
    (anim) => anim.to === player.order
  )
  const money =
    moneyDecreaseAnimation?.money || moneyIncreaseAnimation?.money || 0
  const myAvatar = getPlayerAvatar(player, user)

  const handleCardClick = () => {
    if (getUserCreds().email !== player.id) return
    dispatch(
      setModalData({
        playerCards: { visible: true, playerOrder: player.order },
      })
    )
  }

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        "flex relative justify-between items-center   py-1 md:min-h-[4rem] md:py-0 px-2 md:px-4 shadow-md",
        {
          "grayscale bankrupted": player.isBankrupt,
          "shadow-[inset_0px_0px_51px_#fecc5c] active":
            currentTurn === player.id &&
            !forTradeScreen &&
            !forAuctionScreen &&
            !player.isBankrupt,
          "bg-transparent": forTradeScreen || forAuctionScreen,
          "bg-black": !forTradeScreen && !forAuctionScreen,
        }
      )}
      data-testid={`game.player-tab-${player.order}`}
    >
      {money && !forTradeScreen && !forAuctionScreen ? (
        <div
          className={classNames(
            "px-10 py-5 absolute hidden left-1/2 -translate-x-1/2 z-[100] ",
            { "money-fly-up": moneyIncreaseAnimation }, // Apply animation class conditionally,
            { "money-fly-down": moneyDecreaseAnimation } // Apply animation class conditionally
          )}
        >
          <Money
            iconClass="h-0"
            className={classNames(
              "text-6xl font-poppins gap-2 font-bold drop-shadow-[2px_2px_2px_black]",
              { "text-green-400": moneyIncreaseAnimation },
              { "text-red-500": moneyDecreaseAnimation }
            )}
            sign={moneyIncreaseAnimation ? "+" : "-"}
            money={money}
          ></Money>
        </div>
      ) : null}
      <div className="flex gap-2 items-baseline ">
        {player.disconnected && (
          <SignalSlashIcon color="white" className="h-6 w-6" />
        )}
        <img className="md:h-9 md:w-9 h-9 w-9" src={piece?.imgSrc}></img>
        <div className="flex gap-2 items-center">
          <img
            className="h-11 w-11 rounded-full object-cover md:block hidden"
            src={
              user.profileImage
                ? encodeURI(user.profileImage)
                : myAvatar
                ? myAvatar
                : tempUserImage
            }
          ></img>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2 items-center">
              <div className="flex flex-col gap-1">
                <div className="font-poppins text-white text-base whitespace-nowrap ">
                  {user.username.slice(0, 10) +
                    (user.username.length > 10 ? "..." : "")}
                </div>
                {player.role !== "none" && (
                  <div
                    className="px-2 py-0.5 rounded-md text-xs font-medium font-poppins h-fit whitespace-nowrap w-fit"
                    style={{
                      backgroundColor:
                        gameRoles.find((r) => r.id === player.role)?.color +
                        "40",
                      color: gameRoles.find((r) => r.id === player.role)?.color,
                    }}
                  >
                    {gameRoles.find((r) => r.id === player.role)?.title}
                  </div>
                )}
              </div>

              <img
                className={classNames("h-5 md:h-8 cursor-pointer", {
                  "opacity-0": true,
                })}
                src={images.icons.cards}
                onClick={handleCardClick}
                data-testid={`game.player-cards-btn-${player.order}`}
              ></img>

              {/* <img src={dummyFlag}></img> */}
            </div>
            {/* <div className="text-[#B5B4B4] text-xs font-poppins"> */}
            {/*   Skale Enjoyoor */}
            {/* </div> */}
          </div>
        </div>
      </div>
      {/* <div
            className="text-white text-2xl font-semibold font-poppins"
            data-testid={`game.player-money-${player.order}`}
         > */}

      {player.isBankrupt ? (
        <div className="text-xl text-white font-poppins font-bold">
          Bankrupt
        </div>
      ) : (
        <Money
          money={player.money}
          testId={
            forTradeScreen
              ? `auction.money-${player.order}`
              : `game.player-money-${player.order}`
          }
          iconClass="h-1/2"
          className="text-white text-3xl md:text-3xl font-semibold font-poppins gap-1 leading-none"
        ></Money>
      )}
    </div>
  )
}

export default PlayerListItemInfo
